import React, { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import SettingsPageHeader from 'components/SettingsPageHeader';
import CustomButton from 'components/button/CustomButton';
import {
  PasteDefinitionResult,
  createPasteDefinition,
  deletePasteDefinition,
  getPasteDefinitions,
  updatePasteDefinition,
} from 'stores/ContractPasteStoreAdapter';
import {
  GetContractTemplateResponse,
  getContractTemplate,
} from 'stores/ContractTemplateStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

import PasteDefinitionForm, { PasteDefinitionFormHandle } from './components/PasteDefinitionForm';

export type ColumnCandidate = {
  key: string;
  label: string;
};

const PasteDefinitionManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState<PasteDefinitionResult[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // カラム候補（固定 + 可変）
  const [columnCandidates, setColumnCandidates] = useState<ColumnCandidate[]>([]);

  const formRef = React.useRef<PasteDefinitionFormHandle>(null);

  const selectedDefinition = definitions.find((d) => d.pasteDefinitionId === selectedId) || null;

  const fetchData = useCallback(() => {
    setLoading(true);
    getPasteDefinitions()
      .then((res) => {
        setDefinitions(res.results);
        setLoading(false);
      })
      .catch(() => {
        errorDefault('ペースト定義の取得に失敗しました');
        setLoading(false);
      });
  }, []);

  // カラム候補の取得
  useEffect(() => {
    Promise.all([getMasterType('paste_fixed_column'), getContractTemplate({ visibleOnly: true })])
      .then(([fixedColumns, templateData]: [MasterTypeResult[], GetContractTemplateResponse]) => {
        const fixed: ColumnCandidate[] = fixedColumns.map((col) => ({
          key: col.typeCode,
          label: col.typeLabel,
        }));

        const variable: ColumnCandidate[] = templateData.results.flatMap((group) =>
          group.items
            .filter((item) => !item.isDeleted)
            .map((item) => ({
              key: `contractItems.${item.itemKey}`,
              label: item.label,
            })),
        );

        // 表示名の昇順で統合
        const all = [...fixed, ...variable].sort((a, b) => a.label.localeCompare(b.label, 'ja'));
        setColumnCandidates(all);
      })
      .catch(() => {
        errorDefault('カラム候補の取得に失敗しました');
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = (name: string, columns: string[]) => {
    createPasteDefinition({ name, columns })
      .then((pasteDefinitionId) => {
        successDefault('ペースト定義を登録しました');
        setIsCreateMode(false);
        getPasteDefinitions().then((res) => {
          setDefinitions(res.results);
          setSelectedId(pasteDefinitionId);
        });
      })
      .catch((e: Error) => errorDefault('登録に失敗しました', e.message));
  };

  const handleUpdate = (name: string, columns: string[]) => {
    if (!selectedId) return;
    updatePasteDefinition({ pasteDefinitionId: selectedId, name, columns })
      .then(() => {
        successDefault('ペースト定義を更新しました');
        getPasteDefinitions().then((res) => {
          setDefinitions(res.results);
        });
      })
      .catch((e: Error) => errorDefault('更新に失敗しました', e.message));
  };

  const handleDeleteConfirm = () => {
    if (!selectedId) return;
    deletePasteDefinition({ pasteDefinitionId: selectedId })
      .then(() => {
        successDefault('ペースト定義を削除しました');
        setDeleteConfirmOpen(false);
        setSelectedId(null);
        getPasteDefinitions().then((res) => {
          setDefinitions(res.results);
        });
      })
      .catch((e: Error) => {
        setDeleteConfirmOpen(false);
        errorDefault('削除に失敗しました', e.message);
      });
  };

  // フッター（ConditionListのdetailFooterパターンに準拠）
  const renderFooter = () => {
    if (isCreateMode) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="登録"
            buttonVariant="primary"
            icon={<AddIcon />}
            onClick={() => formRef.current?.submit()}
          />
        </Stack>
      );
    }
    if (selectedDefinition) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="削除"
            buttonVariant="danger"
            icon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
          />
          <CustomButton
            label="更新"
            buttonVariant="primary"
            onClick={() => formRef.current?.submit()}
          />
        </Stack>
      );
    }
    return undefined;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <SettingsPageHeader
        breadcrumbs={[{ label: '契約管理', to: '/contract' }, { label: 'ペースト定義設定' }]}
        icon={<ContentPasteIcon />}
        title="ペースト定義設定"
        description="貼り付け一括入力の項目マッピング定義を管理します"
        mb={1.5}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* 左パネル: 一覧 */}
        <Box
          sx={{
            width: '30%',
            flexShrink: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* ヘッダー */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="body2" color="text.secondary">
              {definitions.length}件
            </Typography>
            <Tooltip title="新規作成">
              <IconButton
                size="small"
                onClick={() => {
                  setIsCreateMode(true);
                  setSelectedId(null);
                }}
                sx={{
                  '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          {/* リスト */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List disablePadding>
              {definitions.map((def) => (
                <ListItemButton
                  key={def.pasteDefinitionId}
                  selected={selectedId === def.pasteDefinitionId}
                  onClick={() => {
                    setSelectedId(def.pasteDefinitionId);
                    setIsCreateMode(false);
                  }}
                  sx={{
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        {def.name}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
              {definitions.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    ペースト定義がありません
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </Box>

        {/* 右パネル: 詳細/作成 */}
        <Box
          sx={{
            flex: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* コンテンツ（スクロール領域） */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {isCreateMode ? (
              <PasteDefinitionForm
                key="create"
                ref={formRef}
                mode="create"
                columnCandidates={columnCandidates}
                onSubmit={handleCreate}
                onCancel={() => setIsCreateMode(false)}
              />
            ) : selectedDefinition ? (
              <PasteDefinitionForm
                key={selectedDefinition.pasteDefinitionId}
                ref={formRef}
                mode="edit"
                initialName={selectedDefinition.name}
                initialColumns={selectedDefinition.columns.map((c) => c.columnKey)}
                columnCandidates={columnCandidates}
                onSubmit={handleUpdate}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  ペースト定義を選択してください
                </Typography>
              </Box>
            )}
          </Box>
          {/* フッター（スクロール領域外に固定表示） */}
          {renderFooter() && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {renderFooter()}
            </Box>
          )}
        </Box>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
        }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={600}>
            ペースト定義を削除
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            「{selectedDefinition?.name}」を削除しますか？この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PasteDefinitionManagement;
